// GST engine — computes the 2-monthly net GST liability and schedules
// each return's payment 28 days after the period end.
//
// CCAANZ is on the **invoice basis**: GST is recognised when the
// invoice is *issued*, not when it's paid. Because the cashflow tool
// uses one timing profile per line for both recognition and payment
// (Steve's call — accuracy ±1 month is acceptable), the GST falls in
// the same month as the underlying income/expense.
//
// Net = sum(GST collected on income) − sum(GST paid on expenses).
// Positive net = pay IRD; negative = refund.

import { parseYm, ymKey } from './timing';

export const GST_FRACTION = 3 / 23; // GST extracted from a GST-inclusive amount

/** Lower-cases boundary semantics: a 2-monthly period is two whole
 *  calendar months, both inclusive. e.g. (Mar, Apr) → 2026-03 and 2026-04.
 *  We identify each period by its END month YYYY-MM (the second month). */
export type GstPeriod = {
  startYm: string; // first month of the period
  endYm: string; // second month of the period
  payYm: string; // month the payment hits the operating account (28th)
};

// CCA's 2-monthly GST periods end on the last day of Feb, Apr, Jun, Aug, Oct, Dec.
// This is CCA's registered filing schedule with IRD — it doesn't change year to year,
// so no configuration is needed. Payment falls on the 28th of the following month
// (Mar, May, Jul, Sep, Nov, Jan).
const GST_PERIOD_END_MIDXS = [1, 3, 5, 7, 9, 11] as const; // 0-indexed month indices

/** Returns all 2-monthly GST periods whose payment month falls within the
 *  given sorted array of YYYY-MM strings, using CCA's registered filing schedule. */
export function generateGstPeriodsForWindow(windowYms: string[]): GstPeriod[] {
  const first = windowYms[0];
  const last = windowYms[windowYms.length - 1];
  if (first === undefined || last === undefined) return [];
  const windowSet = new Set(windowYms);
  const [minY] = parseYm(first);
  const [maxY] = parseYm(last);
  const out: GstPeriod[] = [];
  for (let y = minY - 1; y <= maxY + 1; y++) {
    for (const endMIdx of GST_PERIOD_END_MIDXS) {
      const payYm = ymKey(y, endMIdx + 1);
      if (!windowSet.has(payYm)) continue;
      out.push({
        startYm: ymKey(y, endMIdx - 1),
        endYm: ymKey(y, endMIdx),
        payYm,
      });
    }
  }
  out.sort((a, b) => a.payYm.localeCompare(b.payYm));
  return out;
}

/** Computes the net GST for one period, given monthly income and
 *  expense maps (each ym -> gross amount). Both maps are gross
 *  GST-inclusive amounts; the engine extracts GST_FRACTION from each. */
export function computePeriodNetGst(
  period: GstPeriod,
  incomeGstByYm: Record<string, number>,
  expenseGstByYm: Record<string, number>,
): number {
  const collected = (incomeGstByYm[period.startYm] ?? 0) + (incomeGstByYm[period.endYm] ?? 0);
  const paid = (expenseGstByYm[period.startYm] ?? 0) + (expenseGstByYm[period.endYm] ?? 0);
  return collected - paid;
}
